// src/components/pages/ScratchAlerts.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../layout/AppShell";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Badge from "../ui/Badge";
import Icon from "../ui/Icon";
import Spinner, { Toast } from "../ui/Spinner";
import Field from "../ui/Field";
import ResponsibleGamblingDisclaimer from "../scratch/ResponsibleGamblingDisclaimer";
import {
  getAlerts,
  markAllAlertsRead,
  markAlertRead,
  getPreferences,
  savePreferences,
  getStates,
  formatDate,
} from "../../services/scratchService";

const ALERT_META = {
  NEW_GAME: { icon: "sparkles", variant: "teal", label: "New game" },
  TOP_PRIZE_CLAIMED: { icon: "trophy", variant: "gold", label: "Top prize claimed" },
  NO_TOP_PRIZES: { icon: "ticket-off", variant: "red", label: "No top prizes" },
  SCORE_UP: { icon: "arrow-up", variant: "gold", label: "Score up" },
  SCORE_DOWN: { icon: "arrow-down", variant: "neutral", label: "Score down" },
};

function AlertRow({ alert, onOpen, onRead }) {
  const meta = ALERT_META[alert.alertType] || { icon: "bell", variant: "neutral", label: alert.alertType };
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-xl"
      style={{
        background: alert.read ? "var(--surf-2)" : "var(--gold-dim)",
        border: `1px solid ${alert.read ? "var(--border)" : "var(--gold-border)"}`,
      }}
    >
      <span
        className="inline-flex items-center justify-center rounded-full shrink-0"
        style={{ width: 32, height: 32, background: "var(--surf-3)", color: "var(--txt-2)" }}
      >
        <Icon name={meta.icon} size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[13px]" style={{ color: "var(--txt)" }}>
            {alert.title}
          </span>
          <Badge variant={meta.variant}>{meta.label}</Badge>
        </div>
        <p className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
          {alert.message}
        </p>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-[11px]" style={{ color: "var(--txt-3)" }}>
            {formatDate(alert.createdAt)}
          </span>
          <button
            type="button"
            className="text-[11px]"
            style={{ color: "var(--gold)" }}
            onClick={() => onOpen(alert.scratchGameId)}
          >
            View game
          </button>
          {!alert.read && (
            <button
              type="button"
              className="text-[11px]"
              style={{ color: "var(--txt-2)" }}
              onClick={() => onRead(alert.id)}
            >
              Mark read
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ScratchAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, tone: "success", message: "" });

  // Preferences (the "all states" default row).
  const [stateCode, setStateCode] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [alertNew, setAlertNew] = useState(true);
  const [alertScore, setAlertScore] = useState(true);
  const [saving, setSaving] = useState(false);

  const flash = (message, tone = "success") => {
    setToast({ open: true, tone, message });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 2600);
  };

  const load = async () => {
    setLoading(true);
    const [a, prefs, st] = await Promise.all([getAlerts(), getPreferences(), getStates()]);
    setAlerts(a);
    setStates(st);
    const pref = prefs.find((p) => p.stateCode == null) || prefs[0];
    if (pref) {
      setStateCode(pref.stateCode || "");
      setMinPrice(pref.minTicketPrice ?? "");
      setMaxPrice(pref.maxTicketPrice ?? "");
      setAlertNew(pref.alertOnNewGames);
      setAlertScore(pref.alertOnScoreChange);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleMarkAll = async () => {
    const n = await markAllAlertsRead();
    if (n >= 0) {
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
      flash("All alerts marked read.");
    }
  };

  const handleRead = async (alertId) => {
    const ok = await markAlertRead(alertId);
    if (ok) setAlerts((prev) => prev.map((a) => (a.id === alertId ? { ...a, read: true } : a)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePreferences({
        stateCode: stateCode || null,
        minTicketPrice: minPrice === "" ? null : Number(minPrice),
        maxTicketPrice: maxPrice === "" ? null : Number(maxPrice),
        alertOnNewGames: alertNew,
        alertOnScoreChange: alertScore,
      });
      flash("Preferences saved.");
    } catch {
      flash("Couldn't save preferences.", "error");
    } finally {
      setSaving(false);
    }
  };

  const unread = alerts.filter((a) => !a.read).length;

  return (
    <AppShell>
      <div className="px-4 py-8 space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-semibold flex items-center gap-2" style={{ fontSize: 24, color: "var(--txt)" }}>
              <Icon name="bell" size={22} />
              Scratch Alerts
            </h1>
            <p className="text-[13px] mt-1" style={{ color: "var(--txt-2)" }}>
              Changes in remaining prizes for the games you follow. {unread > 0 && `${unread} unread.`}
            </p>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" icon="circle-check" onClick={handleMarkAll}>
              Mark all read
            </Button>
          )}
        </header>

        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <section className="space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-16" style={{ color: "var(--txt-2)" }}>
                <Spinner size={20} />
              </div>
            ) : alerts.length === 0 ? (
              <Card variant="soft">
                <div className="text-center py-6 space-y-2">
                  <Icon name="bell" size={24} className="text-[var(--txt-3)]" />
                  <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>
                    No alerts yet. Set your preferences and we'll notify you when remaining
                    prizes change.
                  </p>
                </div>
              </Card>
            ) : (
              alerts.map((a) => (
                <AlertRow
                  key={a.id}
                  alert={a}
                  onOpen={(gameId) => navigate(`/scratch/games/${gameId}`)}
                  onRead={handleRead}
                />
              ))
            )}
          </section>

          <section>
            <Card>
              <h3 className="font-semibold text-[15px] mb-4" style={{ color: "var(--txt)" }}>
                Alert preferences
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-[12px] mb-1.5" style={{ color: "var(--txt-2)" }}>
                    State
                  </label>
                  <select
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value)}
                    className="w-full rounded-full px-4 py-2 text-[14px]"
                    style={{
                      background: "var(--bg)",
                      border: "1px solid var(--border)",
                      color: "var(--txt)",
                    }}
                  >
                    <option value="">All states</option>
                    {states.map((s) => (
                      <option key={s.stateCode} value={s.stateCode}>
                        {s.stateCode} — {s.stateName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Min price"
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="$"
                  />
                  <Field
                    label="Max price"
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="$"
                  />
                </div>

                <ToggleRow
                  label="New games added"
                  checked={alertNew}
                  onChange={() => setAlertNew((v) => !v)}
                />
                <ToggleRow
                  label="Score changes on games I follow"
                  checked={alertScore}
                  onChange={() => setAlertScore((v) => !v)}
                />

                <Button full icon="check" onClick={handleSave} loading={saving}>
                  Save preferences
                </Button>
              </div>
            </Card>
          </section>
        </div>

        <ResponsibleGamblingDisclaimer />
      </div>

      <Toast open={toast.open} tone={toast.tone} message={toast.message} />
    </AppShell>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="w-full flex items-center justify-between gap-3 py-1.5"
    >
      <span className="text-[13px] text-left" style={{ color: "var(--txt)" }}>
        {label}
      </span>
      <span
        className="inline-flex items-center rounded-full transition-all shrink-0"
        style={{
          width: 40,
          height: 22,
          padding: 2,
          background: checked ? "var(--gold)" : "var(--surf-3)",
          justifyContent: checked ? "flex-end" : "flex-start",
        }}
      >
        <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff" }} />
      </span>
    </button>
  );
}
